/**
 * Favorites Modal
 * Displays the user's favorite circuits with option to open detail
 */

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { useFavoritesStore } from '@/stores/favoritesStore';
import { useConversationStore } from '@/stores/conversationStore';
import { useUIStore } from '@/stores/uiStore';
import { MapPin, Euro, ExternalLink, HeartOff } from 'lucide-react';

interface FavoritesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function FavoritesModal({ isOpen, onClose }: FavoritesModalProps) {
  const { favoriteCircuits, removeFavorite } = useFavoritesStore();
  const setSelectedCircuit = useConversationStore((s) => s.setSelectedCircuit);
  const setIsCircuitModalOpen = useUIStore((s) => s.setIsCircuitModalOpen);
  const setIsFavoritesModalOpen = useUIStore((s) => s.setIsFavoritesModalOpen);

  const handleViewCircuit = (circuit: (typeof favoriteCircuits)[0]) => {
    setSelectedCircuit(circuit);
    setIsFavoritesModalOpen(false);
    setIsCircuitModalOpen(true);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-hidden flex flex-col bg-white border border-gray-200 rounded-2xl shadow-xl z-[100]">
        <div className="sr-only">
          <DialogTitle>Mes circuits favoris</DialogTitle>
          <DialogDescription>
            Liste des circuits que vous avez mis en favori
          </DialogDescription>
        </div>
        <h2 className="text-xl font-bold text-gray-900">Mes circuits favoris</h2>

        <div className="flex-1 overflow-y-auto mt-4 -mx-1 px-1">
          {favoriteCircuits.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <p className="font-medium">Aucun circuit en favori</p>
              <p className="text-sm mt-2">
                Ajoutez des circuits en cliquant sur le cœur sur les cartes du
                carousel.
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {favoriteCircuits.map((circuit) => (
                <li
                  key={circuit.id}
                  className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-900 truncate">
                      {circuit.name}
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4 shrink-0" />
                        {circuit.destination}
                      </span>
                      <span className="flex items-center gap-1">
                        <Euro className="h-4 w-4 shrink-0" />
                        dès {circuit.price}€
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => removeFavorite(circuit.id)}
                      className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors focus:outline-none focus:ring-2 focus:ring-red-200"
                      aria-label="Retirer des favoris"
                      title="Retirer des favoris"
                    >
                      <HeartOff className="h-5 w-5" aria-hidden />
                    </button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleViewCircuit(circuit)}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Voir
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
